import {
  fetchGetAllCompanyProcedures,
  fetchGetCompanyProcedureFormats,
  fetchGetPriorityCompanies,
  fetchGetPriorityConfig,
} from 'api/priority';
import { store } from 'store';
import type { Procedure } from 'views/priority/priority-settings-page/companies/ProceduresList';

export type PrioritySendDocsGapId = 'connection' | 'companies' | 'procedures' | 'formats';

export type PrioritySendDocsGap = {
  id: PrioritySendDocsGapId;
  settingsPath: string;
};

export type PrioritySendDocsReadinessResult = {
  ready: boolean;
  gaps: PrioritySendDocsGap[];
};

const PRIORITY_SETTINGS_BASE = '/priority';

const GAP_SETTINGS_PATH: Record<PrioritySendDocsGapId, string> = {
  connection: `${PRIORITY_SETTINGS_BASE}/app-links`,
  companies: `${PRIORITY_SETTINGS_BASE}/companies`,
  procedures: `${PRIORITY_SETTINGS_BASE}/company-procedures`,
  formats: `${PRIORITY_SETTINGS_BASE}/procedures`,
};

function hasLegacyPriorityCredentials(): boolean {
  const priority = store.getState().companyPlatforms.priority;
  const hasUrl = Boolean(priority.url?.trim());
  const hasCompany = Boolean(priority.company?.trim());
  const hasAuth = Boolean(
    priority.password?.trim() || priority.apiToken?.trim() || priority.token?.trim(),
  );
  return hasUrl && hasCompany && hasAuth;
}

function isActiveProcedure(procedure: Procedure): boolean {
  return procedure.procStatus !== false;
}

async function hasAnyProcedureFormat(procedures: Procedure[]): Promise<boolean> {
  if (procedures.length === 0) return false;

  // יציאה מוקדמת: מספיק format אחד כדי לקבוע מוכנות. בדיקה רצופה במקום Promise.all
  // שירה קריאת formats לכל פרוצדורה (מאות קריאות מיותרות בלחיצה על אייקון Priority).
  for (const procedure of procedures) {
    const formats = await fetchGetCompanyProcedureFormats(procedure.prCompanyId, procedure.procId);
    if (formats.length > 0) return true;
  }

  return false;
}

// cache קצר כדי שלחיצות חוזרות על אייקון Priority לא יריצו שוב את כל בדיקת המוכנות
// (config + companies + procedures + formats). force=true לרענון מיידי אחרי שינוי הגדרות.
let cachedReadiness: { at: number; result: PrioritySendDocsReadinessResult } | null = null;
const READINESS_CACHE_TTL_MS = 60_000;

export function invalidatePrioritySendDocsReadinessCache(): void {
  cachedReadiness = null;
}

export async function checkPrioritySendDocsReadiness(
  opts?: { force?: boolean },
): Promise<PrioritySendDocsReadinessResult> {
  if (!opts?.force && cachedReadiness && Date.now() - cachedReadiness.at < READINESS_CACHE_TTL_MS) {
    return cachedReadiness.result;
  }

  const gaps: PrioritySendDocsGap[] = [];

  const [config, companies, procedures] = await Promise.all([
    fetchGetPriorityConfig(),
    fetchGetPriorityCompanies(),
    fetchGetAllCompanyProcedures(),
  ]);

  const hasApiConnection = Boolean(
    config?.priorityApiUrl?.trim() || config?.priorityFullApiUrl?.trim(),
  );

  if (!hasApiConnection && !hasLegacyPriorityCredentials()) {
    gaps.push({ id: 'connection', settingsPath: GAP_SETTINGS_PATH.connection });
  }

  const activeCompanies = (companies ?? []).filter((company) => company.companyStatus !== false);
  if (activeCompanies.length === 0) {
    gaps.push({ id: 'companies', settingsPath: GAP_SETTINGS_PATH.companies });
  }

  const activeProcedures = (procedures ?? []).filter(isActiveProcedure);
  if (activeProcedures.length === 0) {
    gaps.push({ id: 'procedures', settingsPath: GAP_SETTINGS_PATH.procedures });
  } else {
    const hasFormats = await hasAnyProcedureFormat(activeProcedures);
    if (!hasFormats) {
      gaps.push({ id: 'formats', settingsPath: GAP_SETTINGS_PATH.formats });
    }
  }

  const result: PrioritySendDocsReadinessResult = {
    ready: gaps.length === 0,
    gaps,
  };
  cachedReadiness = { at: Date.now(), result };
  return result;
}

export function getPrimaryPrioritySettingsPath(gaps: PrioritySendDocsGap[]): string {
  return gaps[0]?.settingsPath ?? `${PRIORITY_SETTINGS_BASE}/app-links`;
}
